import {
  IAutoclickerGameCreatePayload,
  IAutoclickerGameUpdatePayload,
  TAutoclickerCountryRecord,
  TAutoclickerGameData,
  TAutoclickerTemplate,
  IAutoclickerJobDetailsRecord,
  IAutoclickerJobDetailsResponse,
  IAutoclickerJobsPayload,
  IAutoclickerJobsStatsData,
  TAutoclickerAvatarRecord,
  TAutoclickerVoiceRecord,
  TAutoclickerScriptRecord,
  TAutoclickerScriptPayload
} from '@/api/schema';
import { http } from '@/api/Connect';
import { API_VERSION, BASE } from '@/api/CopyStake/BaseUrls';
import { AxiosResponse } from 'axios';
import { buildUrl } from '@/helpers/buildUrl';
import {
  EAutoclickerStatus,
  TAiStreamer,
  TAiStreamerPayload,
  TAutoClicker
} from '@/views/CopyStake/CopyStakeAutoclicker/types';

const buildAutoclickerUrl = (
  type: string,
  version: API_VERSION = API_VERSION.V1
): string => {
  return buildUrl(`${BASE(version)}/{operatorId}/autoclicker/${type}`);
};

const API_TEMPLATES_LINK = (): string => buildAutoclickerUrl('templates');
const API_GAMES_LINK = (): string => buildAutoclickerUrl('games');
const API_LIST_LINK = (): string => buildAutoclickerUrl('config/list');
const API_CONFIG_LINK = (): string => buildAutoclickerUrl('config');
const API_COUNTRIES_LINK = (): string =>
  buildAutoclickerUrl('config/countries');
const API_JOBS_LINK = (): string => buildAutoclickerUrl('jobs');
const API_AVATAR_LINK = (): string => buildAutoclickerUrl('avatars');
const API_VOICES_LINK = (): string => buildAutoclickerUrl('voices');
const API_SCRIPT_LINK = (): string => buildAutoclickerUrl('script');

const API_CONFIG_V2_LINK = (): string =>
  buildAutoclickerUrl('config', API_VERSION.V2);

export const getAutoclickerTemplates = (): Promise<TAutoclickerTemplate[]> => {
  return http
    .get(API_TEMPLATES_LINK(), {
      cacheTime: 5000
    })
    .then(({ data }: AxiosResponse) => data);
};

export const getAutoclickerList = (): Promise<TAutoClicker[]> => {
  return http
    .get(API_LIST_LINK(), {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse) => data);
};

export const createAutoclickerConfig = (
  payload: TAutoClicker
): Promise<unknown> => {
  return http.post(API_CONFIG_LINK(), payload);
};

export const getAutoclickerConfigDetails = (
  autoclickerId: number | string
): Promise<TAutoClicker> => {
  return http
    .get(`${API_CONFIG_LINK()}/${autoclickerId}`, {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): TAutoClicker => data);
};

export const updateAutoclickerConfig = (
  payload: TAutoClicker
): Promise<unknown> => {
  return http.put(`${API_CONFIG_LINK()}/${payload.id}`, payload);
};

export const deleteAutoclickerConfig = (
  payload: TAutoClicker
): Promise<unknown> => {
  return http.delete(`${API_CONFIG_LINK()}/${payload.id}`);
};

export const toggleAutoclickerConfig = (
  payload: TAutoClicker
): Promise<void> => {
  return http.patch(`${API_CONFIG_LINK()}/${payload.id}/status`, null, {
    params: {
      status:
        payload.status === EAutoclickerStatus.ACTIVE
          ? EAutoclickerStatus.INACTIVE
          : EAutoclickerStatus.ACTIVE
    }
  });
};

export const getAutoclickerGames = (): Promise<TAutoclickerGameData[]> => {
  return http
    .get(API_GAMES_LINK(), {
      cacheTime: 2000
    })
    .then(({ data }: AxiosResponse): TAutoclickerGameData[] => data);
};

export const createAutoclickerGame = (
  payload: IAutoclickerGameCreatePayload
): Promise<TAutoclickerGameData> => {
  return http
    .post(API_GAMES_LINK(), payload)
    .then(({ data }: AxiosResponse): TAutoclickerGameData => data);
};

export const updateAutoclickerGame = (
  id: number | string,
  payload: IAutoclickerGameUpdatePayload
): Promise<TAutoclickerGameData> => {
  return http
    .put(`${API_GAMES_LINK()}/${id}`, payload)
    .then(({ data }: AxiosResponse): TAutoclickerGameData => data);
};

export const deleteAutoclickerGame = (
  id: number | string
): Promise<unknown> => {
  return http.delete(`${API_GAMES_LINK()}/${id}`);
};

export const getAutoclickerCountriesList = (): Promise<
  TAutoclickerCountryRecord[]
> => {
  return http
    .get(API_COUNTRIES_LINK(), {
      cacheTime: 5000
    })
    .then(({ data }: AxiosResponse): TAutoclickerCountryRecord[] => data);
};

export const getAutoclickerJobs = (
  params: IAutoclickerJobsPayload
): Promise<IAutoclickerJobDetailsResponse> => {
  return http
    .get(API_JOBS_LINK(), {
      params,
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): IAutoclickerJobDetailsResponse => data);
};

export const getAutoclickerJobDetails = (
  jobId: number | string
): Promise<IAutoclickerJobDetailsRecord> => {
  return http
    .get(`${API_JOBS_LINK()}/${jobId}`, {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): IAutoclickerJobDetailsRecord => data);
};

export const getAutoclickerJobsStats =
  (): Promise<IAutoclickerJobsStatsData> => {
    return http
      .get(`${API_JOBS_LINK()}/statistics`, {
        cacheTime: 0
      })
      .then(({ data }: AxiosResponse): IAutoclickerJobsStatsData => data);
  };

export const stopAutoclickerJob = (
  jobId: number | string,
  force: boolean
): Promise<unknown> => {
  return http
    .post(`${API_JOBS_LINK()}/${jobId}/stop`, null, { params: { force } })
    .then(({ data }: AxiosResponse): unknown => data);
};

export const getAutoclickerAvatarsList = (): Promise<
  TAutoclickerAvatarRecord[]
> => {
  return http
    .get(API_AVATAR_LINK(), {
      cacheTime: 5000
    })
    .then(({ data }: AxiosResponse): TAutoclickerAvatarRecord[] => data);
};

export const getAutoclickerVoicesList = (): Promise<
  TAutoclickerVoiceRecord[]
> => {
  return http
    .get(API_VOICES_LINK(), {
      cacheTime: 5000
    })
    .then(({ data }: AxiosResponse): TAutoclickerVoiceRecord[] => data);
};

export const getAutoclickerScriptList = (
  language: string
): Promise<TAutoclickerScriptRecord[]> => {
  return http
    .get(`${API_SCRIPT_LINK()}/language/${language}`, {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): TAutoclickerScriptRecord[] => data);
};

export const getAutoclickerScript = (
  id: number | string
): Promise<TAutoclickerScriptRecord> => {
  return http
    .get(`${API_SCRIPT_LINK()}/${id}`, {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): TAutoclickerScriptRecord => data);
};

export const createAutoclickerScript = (
  payload: TAutoclickerScriptPayload
): Promise<TAutoclickerScriptRecord> => {
  return http
    .post(API_SCRIPT_LINK(), payload)
    .then(({ data }: AxiosResponse): TAutoclickerScriptRecord => data);
};

export const updateAutoclickerScript = (
  id: number | string,
  payload: TAutoclickerScriptPayload
): Promise<TAutoclickerScriptRecord> => {
  return http
    .put(`${API_SCRIPT_LINK()}/${id}`, payload)
    .then(({ data }: AxiosResponse): TAutoclickerScriptRecord => data);
};

export const deleteAutoclickerScript = (
  id: number | string
): Promise<unknown> => {
  return http.delete(`${API_SCRIPT_LINK()}/${id}`);
};

export const createAiStreamerConfig = (
  payload: TAiStreamerPayload
): Promise<TAiStreamer> => {
  return http
    .post(API_CONFIG_V2_LINK(), payload)
    .then(({ data }: AxiosResponse<TAiStreamer>) => data);
};
